import * as XLSX from 'xlsx';

export interface ExportData {
  [key: string]: any;
}

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

/**
 * Export data to CSV format
 */
export const exportToCsv = (data: ExportData[], columns: ColumnConfig[], filename: string = 'export.csv') => {
  // Get only visible columns
  const visibleColumns = columns.filter(col => col.visible);
  
  // Create CSV headers
  const headers = visibleColumns.map(col => col.label);
  
  // Create CSV rows
  const rows = data.map(item => 
    visibleColumns.map(col => {
      const value = item[col.key];
      // Handle different data types
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      // Escape commas and quotes in CSV
      const stringValue = String(value || '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    })
  );
  
  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export data to Excel format
 */
export const exportToExcel = (data: ExportData[], columns: ColumnConfig[], filename: string = 'export.xlsx') => {
  // Get only visible columns
  const visibleColumns = columns.filter(col => col.visible);
  
  // Prepare data for Excel
  const excelData = data.map(item => {
    const row: { [key: string]: any } = {};
    visibleColumns.forEach(col => {
      const value = item[col.key];
      if (value instanceof Date) {
        row[col.label] = value.toLocaleString();
      } else if (typeof value === 'object' && value !== null) {
        row[col.label] = JSON.stringify(value);
      } else {
        row[col.label] = value || '';
      }
    });
    return row;
  });
  
  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Set column widths
  const columnWidths = visibleColumns.map(col => ({
    wch: Math.max(col.label.length, 15) // Minimum width of 15 characters
  }));
  worksheet['!cols'] = columnWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Generate and download file
  XLSX.writeFile(workbook, filename);
};

/**
 * Generate filename with timestamp
 */
export const generateFilename = (baseName: string, extension: string): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  return `${baseName}_${timestamp}.${extension}`;
};
